/**
 * Templates AI Edit Selectors
 *
 * Memoized selectors for accessing template build state.
 */

import type { RootState } from '@store/types';

import { draftTemplateSignature, type TemplateBuildState } from '../types';

// ============================================================================
// Base Selector
// ============================================================================

const selectTemplateBuildState = (state: RootState): TemplateBuildState | undefined =>
  state['templatesAiEdit/ui'];

// ============================================================================
// Session Selectors
// ============================================================================

export const selectTaskId = (state: RootState): string | null =>
  selectTemplateBuildState(state)?.taskId ?? null;

export const selectThreadId = (state: RootState): string | null =>
  selectTemplateBuildState(state)?.threadId ?? null;

// ============================================================================
// Build Status Selectors
// ============================================================================

export const selectBuildStatus = (state: RootState) =>
  selectTemplateBuildState(state)?.buildStatus ?? 'idle';

export const selectProgressMessage = (state: RootState) =>
  selectTemplateBuildState(state)?.progressMessage ?? null;

export const selectDocumentTitle = (state: RootState) =>
  selectTemplateBuildState(state)?.documentTitle ?? null;

export const selectDocumentSummary = (state: RootState) =>
  selectTemplateBuildState(state)?.documentSummary ?? null;

export const selectCreatedTemplateId = (state: RootState) =>
  selectTemplateBuildState(state)?.createdTemplateId ?? null;

export const selectSourceTemplateId = (state: RootState) =>
  selectTemplateBuildState(state)?.sourceTemplateId ?? null;

export const selectNeedsStatusRefetch = (state: RootState) =>
  selectTemplateBuildState(state)?.needsStatusRefetch ?? false;

export const selectIsBuildProcessing = (state: RootState) => {
  const status = selectBuildStatus(state);
  return status === 'processing' || status === 'uploading' || status === 'structure_ready';
};

export const selectIsBuildReady = (state: RootState) =>
  selectBuildStatus(state) === 'ready_for_review';

export const selectIsBuildComplete = (state: RootState) =>
  selectBuildStatus(state) === 'completed';

export const selectIsBuildFailed = (state: RootState) =>
  selectBuildStatus(state) === 'failed';

// ============================================================================
// Draft Template Selectors
// ============================================================================

export const selectDraftTemplate = (state: RootState) =>
  selectTemplateBuildState(state)?.draftTemplate ?? null;

export const selectDraftTemplateSections = (state: RootState) =>
  selectTemplateBuildState(state)?.draftTemplate?.sections ?? [];

export const selectDraftTemplateTitle = (state: RootState) =>
  selectTemplateBuildState(state)?.draftTemplate?.title ?? null;

export const selectDraftTemplateDescription = (state: RootState) =>
  selectTemplateBuildState(state)?.draftTemplate?.description ?? null;

export const selectSavedDraftSignature = (state: RootState) =>
  selectTemplateBuildState(state)?.savedDraftSignature ?? null;

/** Whether this session has published at least once. */
export const selectHasSavedOnce = (state: RootState) =>
  selectSavedDraftSignature(state) !== null;

/**
 * Whether the draft on screen is exactly what was last published. False while
 * the session has never saved, so the first save is always available.
 */
export const selectIsDraftSaved = (state: RootState) => {
  const saved = selectSavedDraftSignature(state);
  if (saved === null) return false;

  const draft = selectDraftTemplate(state);
  return draft !== null && draftTemplateSignature(draft) === saved;
};

// ============================================================================
// Section Selection Selectors
// ============================================================================

export const selectSelectedSectionId = (state: RootState) =>
  selectTemplateBuildState(state)?.selectedSectionId ?? null;

export const selectSelectedSection = (state: RootState) => {
  const sections = selectDraftTemplateSections(state);
  const selectedId = selectSelectedSectionId(state);
  if (!selectedId || sections.length === 0) return null;
  return sections.find(s => s.id === selectedId) ?? null;
};

// ============================================================================
// Message Selectors
// ============================================================================

export const selectMessages = (state: RootState) =>
  selectTemplateBuildState(state)?.messages ?? [];

// ============================================================================
// Assistant State Selectors
// ============================================================================

export const selectAssistantStatus = (state: RootState) =>
  selectTemplateBuildState(state)?.assistant.status ?? 'idle';

export const selectCurrentStep = (state: RootState) =>
  selectTemplateBuildState(state)?.assistant.currentStep ?? null;

export const selectStreamingContent = (state: RootState) =>
  selectTemplateBuildState(state)?.assistant.streamingContent ?? '';

export const selectFirstTokenReceived = (state: RootState) =>
  selectTemplateBuildState(state)?.assistant.firstTokenReceived ?? false;

export const selectCurrentMessageId = (state: RootState) =>
  selectTemplateBuildState(state)?.assistant.currentMessageId ?? null;

// ============================================================================
// Activity Selectors
// ============================================================================

export const selectActivities = (state: RootState) =>
  selectTemplateBuildState(state)?.activities ?? {};

export const selectActivitiesArray = (state: RootState) =>
  Object.values(selectTemplateBuildState(state)?.activities ?? {});

// ============================================================================
// Interrupt Selectors
// ============================================================================

export const selectInterrupt = (state: RootState) =>
  selectTemplateBuildState(state)?.interrupt ?? null;

export const selectInterruptType = (state: RootState) =>
  selectTemplateBuildState(state)?.interrupt?.eventType ?? null;

export const selectIsInterruptActive = (state: RootState) =>
  selectTemplateBuildState(state)?.interrupt?.isActive ?? false;

export const selectInterruptId = (state: RootState) =>
  selectTemplateBuildState(state)?.interrupt?.interruptId ?? null;

export const selectInterruptOptions = (state: RootState) =>
  selectTemplateBuildState(state)?.interrupt?.options ?? [];

export const selectInterruptSelectedOptionIds = (state: RootState) =>
  selectTemplateBuildState(state)?.interrupt?.selectedOptionIds ?? [];

export const selectInterruptMessage = (state: RootState) =>
  selectTemplateBuildState(state)?.interrupt?.message ?? null;

export const selectInterruptQuestion = (state: RootState) =>
  selectTemplateBuildState(state)?.interrupt?.question ?? null;

export const selectInterruptAllowMultiple = (state: RootState) =>
  selectTemplateBuildState(state)?.interrupt?.allowMultiple ?? false;

// ============================================================================
// UI State Selectors
// ============================================================================

export const selectIsChatVisible = (state: RootState) =>
  selectTemplateBuildState(state)?.isChatVisible ?? true;

// ============================================================================
// Error Selectors
// ============================================================================

export const selectError = (state: RootState) =>
  selectTemplateBuildState(state)?.error ?? null;

// ============================================================================
// SSE Performance Selectors
// ============================================================================

export const selectSSEPerformanceMetrics = (state: RootState) =>
  selectTemplateBuildState(state)?.ssePerformanceMetrics ?? null;

// ============================================================================
// Composite Selectors
// ============================================================================

/**
 * Select whether the chat is ready for interaction
 */
export const selectIsChatReady = (state: RootState) => {
  const buildStatus = selectBuildStatus(state);
  const taskId = selectTaskId(state);
  return taskId !== null && buildStatus === 'ready_for_review';
};

/**
 * Select whether the assistant is busy
 */
export const selectIsAssistantBusy = (state: RootState) => {
  const status = selectAssistantStatus(state);
  return status === 'thinking' || status === 'streaming';
};

